import { Box, Skeleton } from "@mui/material";
import { useIsFetching } from "@tanstack/react-query";

import { CLIP_PATHS, PREVIEW_HEIGHT } from "../constants";
import type { TextComposerPreviewContent } from "@/features/TextComposerV2/preview/useMessagePreview";
import type { FrameVariant } from "@/types/dragon-tiles";

import { useAccountSite } from "@/api/siteDetails";
import {
  previewHasRenderableBody,
  getTransformedContentFromContentJson,
} from "@/features/TextComposerV2/preview/previewContentUtils";
import { TextCardPreviewMockup } from "@/features/TextComposerV2/preview/TextCardPreviewMockup";

const SCALE_FACTOR = 0.85;

export interface TextCardPreviewProps {
  siteId: string;
  selectedVariant: FrameVariant;
  initialPreviewContent?: TextComposerPreviewContent | null;
}

export function TextCardPreview({
  siteId,
  selectedVariant,
  initialPreviewContent,
}: TextCardPreviewProps) {
  const { data: site } = useAccountSite(siteId);
  const isBulkPreviewsFetching =
    useIsFetching({
      queryKey: ["bulkFrameVariantPreviews", siteId],
    }) > 0;
  const isWaitingForBulkPreview =
    isBulkPreviewsFetching && initialPreviewContent === undefined;

  const isMms = selectedVariant.medium === "mms";
  const activeChannel = isMms ? ("MMS" as const) : ("SMS" as const);
  const fallbackContent = getTransformedContentFromContentJson(
    selectedVariant.content_json,
  );
  const previewContent = initialPreviewContent ?? fallbackContent;

  const previewByChannel = {
    RCS: null,
    SMS: isMms ? null : previewContent,
    MMS: isMms ? previewContent : null,
  };

  const activeContent = isMms ? previewByChannel.MMS : previewByChannel.SMS;
  const readOnlyPreviewIsEmpty = !previewHasRenderableBody(activeContent);

  const mediaNameByUrl: Record<string, string> = {};
  if (previewContent?.body?.type === "IMAGE" && previewContent.body.url) {
    mediaNameByUrl[previewContent.body.url] = "Attached media";
  }

  if (isWaitingForBulkPreview) {
    return (
      <Box
        sx={{
          backgroundColor: "common.white",
          clipPath: CLIP_PATHS.top,
          height: PREVIEW_HEIGHT,
        }}
      >
        <Skeleton variant="queen" width="100%" height="100%" />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "common.white",
        clipPath: CLIP_PATHS.top,
        height: `${PREVIEW_HEIGHT}px`,
        overflow: "hidden",
        display: "flex",
        justifyContent: "center",
        position: "relative",
      }}
    >
      <Box
        sx={{
          transform: `scale(${SCALE_FACTOR})`,
          transformOrigin: "top center",
          pointerEvents: "none",
          position: "absolute",
          top: 10,
        }}
      >
        <TextCardPreviewMockup
          activeChannel={activeChannel}
          previewByChannel={previewByChannel}
          mediaNameByUrl={mediaNameByUrl}
          brandName={site?.brand_name}
          readOnlyPreviewIsEmpty={readOnlyPreviewIsEmpty}
        />
      </Box>
    </Box>
  );
}
